export type NavLink = {
  label: string
  href: string
}

export const navLinks: NavLink[] = [
  { label: 'The lab', href: '#lab' },
  { label: 'Difference', href: '#difference' },
  { label: 'Our story', href: '#story' },
  { label: 'Feedback', href: '#feedback' },
]

/**
 * PLACEHOLDER PORTFOLIO ENTRIES.
 * These three slots are intentionally empty and easy to replace.
 * Fill in `name`, `icon`, `screenshot`, `description` and `storeURL` when
 * an app is ready to share. Leave the fields as empty strings until then —
 * the UI falls back to the neutral label and "Coming soon".
 */
export type AppPlaceholder = {
  slot: string
  label: string
  name: string
  icon: string
  screenshot: string
  description: string
  storeURL: string
}

export const appPlaceholders: AppPlaceholder[] = [
  {
    slot: '01',
    label: 'App 01',
    name: '',
    icon: '',
    screenshot: '',
    description: '',
    storeURL: '',
  },
  {
    slot: '02',
    label: 'App 02',
    name: '',
    icon: '',
    screenshot: '',
    description: '',
    storeURL: '',
  },
  {
    slot: '03',
    label: 'App 03',
    name: '',
    icon: '',
    screenshot: '',
    description: '',
    storeURL: '',
  },
]

export type Principle = {
  title: string
  caption: string
}

/** The four questions every idea answers, shown as the dark strip. */
export const principles: Principle[] = [
  { title: 'Design', caption: 'Coherent, considered and easy to explain.' },
  { title: 'Usability', caption: 'The core value, without avoidable confusion.' },
  { title: 'Feasibility', caption: 'Scope we can build, support and sustain.' },
  { title: 'Quality', caption: 'A standard we can test and maintain.' },
]

export type Differentiator = {
  title: string
  detail: string
}

export const differentiators: Differentiator[] = [
  {
    title: 'Purpose before features',
    detail:
      'Every app has to answer one question first: what is this genuinely for? The feature list comes after.',
  },
  {
    title: 'Considered design',
    detail:
      'The important choices are deliberate, and we can explain why each of them helps the person using it.',
  },
  {
    title: 'Simple to use',
    detail:
      'People should reach the core value on their first try, without a tour or a manual to get there.',
  },
  {
    title: 'Built to last',
    detail:
      'Quality means the main experience stays dependable — testable, maintainable and worth returning to.',
  },
  {
    title: 'A focused portfolio',
    detail:
      'Learning, lifestyle and gaming are different rooms in the same studio. Each gets real attention.',
  },
  {
    title: 'Better with feedback',
    detail:
      'What people tell us shapes what improves next. Not every request becomes a feature; every pattern gets looked at.',
  },
]

export type FeedbackStep = {
  number: string
  title: string
  detail: string
}

export const feedbackLoop: FeedbackStep[] = [
  { number: '01', title: 'Listen', detail: 'A clear route for feedback, and attention to where people get stuck.' },
  { number: '02', title: 'Understand', detail: 'Find the pattern. Separate the underlying need from the suggested fix.' },
  { number: '03', title: 'Prioritize', detail: 'Weigh user value, quality impact and what we can genuinely support.' },
  { number: '04', title: 'Improve', detail: 'Make one focused change, then check whether it actually helped.' },
]

export type ExplorationArea = {
  name: string
  detail: string
}

export const explorationAreas: ExplorationArea[] = [
  { name: 'Learning', detail: 'Tools that help people understand something new and keep going.' },
  { name: 'Lifestyle', detail: 'Quiet support for the ordinary parts of a day.' },
  { name: 'Gaming', detail: 'Play that feels worth the time it asks for.' },
]

export type FaqItem = {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: 'What kinds of apps do you build?',
    answer:
      'Three areas: learning, lifestyle and gaming. Different categories, the same care in how each one is designed and maintained.',
  },
  {
    question: 'Who are they built for?',
    answer:
      'People in the United States. That is our audience focus, and it shapes the language, pricing and everyday details of each app.',
  },
  {
    question: 'What matters most?',
    answer:
      'Quality and feedback. Before a build starts, an idea has to hold up against design, usability, feasibility and quality. After it ships, what people tell us guides what improves next.',
  },
  {
    question: 'Why Black Comet?',
    answer:
      'The studio is named in memory of the founder’s black cat. He is the reason the name exists, and the reason a small comet star sits at the end of the tail.',
  },
]
